/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  AppBar,
  Stack,
  Toolbar,
  useTheme,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { ReleaseForm } from '../components/ReleaseContent/ReleaseForm';
import { CustomerValue } from '../components/ReleaseContent/CustomerValue';
import { RDActivities } from '../components/ReleaseContent/RDActivities';
import { SL3Tickets } from '../components/ReleaseContent/SL3Tickets';
import { StabilizationBugs } from '../components/ReleaseContent/StabilizationBugs';
import { Analytics } from '../components/Dashboard/Analytics';
import { FiltersDrawer } from '../components/common/FiltersDrawer';
import { SortControls } from '../components/common/SortControls';
import { SearchBar } from '../components/common/SearchBar';
import { SectionNavigation } from '../components/ReleaseContent/SectionNavigation';
import { useRelease } from '../hooks/useRelease';
import type { ReleaseResponse } from '../types/release';

export const ReleasePage: React.FC = () => {
  const [releaseData, setReleaseData] = useState<ReleaseResponse | null>(null);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [allExpanded, setAllExpanded] = useState(true);
  const theme = useTheme();

  const customerValueRef = useRef<HTMLDivElement>(null);
  const rdActivitiesRef = useRef<HTMLDivElement>(null);
  const sl3TicketsRef = useRef<HTMLDivElement>(null);
  const stabilizationBugsRef = useRef<HTMLDivElement>(null);

  const handleReleaseGenerated = (data: ReleaseResponse) => {
    setReleaseData(data);
  };

  const {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    filterOptions,
    filteredCustomerValue,
    filteredRDActivities,
    filteredSL3Tickets,
    filteredStabilizationBugs,
  } = useRelease(
    releaseData?.customer_value || [],
    releaseData?.rd_activities || [],
    releaseData?.sl3_tickets || [],
    releaseData?.stabilization_bugs || []
  );

  // Scroll to section when tab is clicked
  useEffect(() => {
    if (!currentSection) return;

    const refs: Record<string, React.RefObject<HTMLDivElement>> = {
      customer: customerValueRef,
      rd: rdActivitiesRef,
      sl3: sl3TicketsRef,
      stabilization: stabilizationBugsRef,
    };

    const ref = refs[currentSection];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentSection]);

  const sections = [
    {
      id: 'customer',
      label: 'Customer Value',
      count: filteredCustomerValue.length,
      ref: customerValueRef,
    },
    {
      id: 'rd',
      label: 'R&D Activities',
      count: filteredRDActivities.length,
      ref: rdActivitiesRef,
    },
    {
      id: 'sl3',
      label: 'SL3 Tickets',
      count: filteredSL3Tickets.length,
      ref: sl3TicketsRef,
    },
    {
      id: 'stabilization',
      label: 'Stabilization Bugs',
      count: filteredStabilizationBugs.length,
      ref: stabilizationBugsRef,
    },
  ].filter((s) => s.count > 0);

  return (
    <Box sx={{ pb: 3 }}>
      <Typography variant="h4" gutterBottom>
        Release Content
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate and view release content
      </Typography>

      <ReleaseForm onReleaseGenerated={handleReleaseGenerated} />

      {releaseData && (
        <Stack spacing={4} sx={{ mt: 2 }}>
          {/* Analytics Dashboard */}
          <Box>
            <Analytics
              filteredCustomerValue={filteredCustomerValue}
              filteredRDActivities={filteredRDActivities}
              filteredSL3Tickets={filteredSL3Tickets}
              filteredStabilizationBugs={filteredStabilizationBugs}
              filteredStabilizationStats={releaseData.stabilization_stats}
              release={releaseData.release}
              codeFreezeDate={releaseData.summary.code_freeze_date}
            />
          </Box>

          {/* Sticky Controls Bar */}
          <AppBar
            position="sticky"
            elevation={1}
            sx={{
              top: { xs: 64, md: 64 }, // Below main AppBar
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              zIndex: theme.zIndex.appBar - 1,
            }}
          >
            <Toolbar sx={{ gap: 2, py: 1.5, flexWrap: 'wrap', minHeight: '64px !important' }}>
              <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 300 }, maxWidth: { md: 400 } }}>
                <SearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <SortControls
                  sortOption={sortOption}
                  onSortChange={setSortOption}
                />
                <IconButton
                  onClick={() => setFiltersDrawerOpen(true)}
                  color="primary"
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                  aria-label="open filters"
                >
                  <FilterList />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Section Navigation */}
          {sections.length > 0 && (
            <SectionNavigation
              currentSection={currentSection}
              onSectionChange={setCurrentSection}
              sections={sections.map((s) => ({ id: s.id, label: s.label, count: s.count }))}
              allExpanded={allExpanded}
              onToggleExpand={() => setAllExpanded(!allExpanded)}
            />
          )}

          {/* Results */}
          <Box sx={{ position: 'relative' }}>
            {filteredCustomerValue.length > 0 && (
              <Box ref={customerValueRef} sx={{ mb: 6 }}>
                <CustomerValue customerValue={filteredCustomerValue} />
              </Box>
            )}
            {filteredRDActivities.length > 0 && (
              <Box ref={rdActivitiesRef} sx={{ mb: 6 }}>
                <RDActivities rdActivities={filteredRDActivities} />
              </Box>
            )}
            {filteredSL3Tickets.length > 0 && (
              <Box ref={sl3TicketsRef} sx={{ mb: 6 }}>
                <SL3Tickets sl3Tickets={filteredSL3Tickets} />
              </Box>
            )}
            {filteredStabilizationBugs.length > 0 && (
              <Box ref={stabilizationBugsRef} sx={{ mb: 6 }}>
                <StabilizationBugs
                  bugs={filteredStabilizationBugs}
                  stats={releaseData.stabilization_stats}
                />
              </Box>
            )}
          </Box>

          {/* Filters Drawer */}
          <FiltersDrawer
            open={filtersDrawerOpen}
            onClose={() => setFiltersDrawerOpen(false)}
            filters={filters}
            filterOptions={filterOptions}
            onFiltersChange={setFilters}
          />
        </Stack>
      )}

      {!releaseData && (
        <Alert
          severity="info"
          sx={{
            mt: 5,
            '& .MuiAlert-message': {
              color: 'text.primary',
            },
          }}
        >
          Enter a release identifier (format: YY.MM) and click Generate to view release content.
        </Alert>
      )}
    </Box>
  );
};
